/**
 * Verified connectivity.
 *
 * `navigator.onLine` reports only whether the OS thinks a network interface is
 * up. It says nothing about whether our API is reachable, and it false-negatives
 * in the desktop webview (WKWebView serving `tauri://localhost` has no origin
 * network of its own) and behind some VPNs — which is how a user ended up with a
 * permanent "You're offline" banner on a perfectly good connection.
 *
 * That flag was also the trigger for the desktop read-cache to start replaying
 * stored responses, so a false negative did not just mislabel the UI: it swapped
 * live data for stale data. Treating "offline" as a fact worth confirming is
 * therefore a correctness fix, not a cosmetic one.
 *
 * Policy here: online is the default, and we only declare offline once a probe
 * of the API has actually failed. A browser `offline` event triggers a probe
 * rather than a verdict; an `online` event is trusted immediately (it can only
 * make things better) and confirmed on the next tick.
 */

import { config } from '@/src/config';

type Listener = (offline: boolean) => void;

const PROBE_TIMEOUT_MS = 6000;
// How often to re-check while we believe we are offline.
const RECHECK_INTERVAL_MS = 15000;

let offline = false;
let started = false;
let recheckTimer: ReturnType<typeof setInterval> | null = null;
let inFlight: Promise<boolean> | null = null;
const listeners = new Set<Listener>();

function probeUrl(): string {
  const base = (config.weaveinApi.baseUrl || config.api.baseUrl || '').replace(/\/$/, '');
  // An empty base means the web app is talking to its own origin through the
  // Vercel proxy; `/health` is rewritten to the API there.
  return `${base}/health`;
}

function setOffline(next: boolean): void {
  if (next === offline) return;
  offline = next;
  listeners.forEach((l) => {
    try {
      l(offline);
    } catch {
      /* a bad listener must not break the others */
    }
  });
  if (offline) startRecheck();
  else stopRecheck();
}

/**
 * Can we reach the API? `no-cors` keeps this free of CORS setup — we only care
 * that the server answered at all, not what it said, so an opaque response is a
 * success and only a transport failure counts as offline.
 */
export async function probeConnectivity(): Promise<boolean> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      await fetch(probeUrl(), {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      setOffline(false);
      return true;
    } catch {
      setOffline(true);
      return false;
    } finally {
      clearTimeout(timer);
      inFlight = null;
    }
  })();

  return inFlight;
}

function startRecheck(): void {
  if (recheckTimer || typeof window === 'undefined') return;
  recheckTimer = setInterval(() => {
    void probeConnectivity();
  }, RECHECK_INTERVAL_MS);
}

function stopRecheck(): void {
  if (recheckTimer) {
    clearInterval(recheckTimer);
    recheckTimer = null;
  }
}

function ensureStarted(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('offline', () => {
    // The OS thinks we dropped. Confirm before telling the user — and before
    // any cache starts standing in for the network.
    void probeConnectivity();
  });
  window.addEventListener('online', () => {
    setOffline(false);
    void probeConnectivity();
  });

  // If the flag is already down at boot, verify it rather than opening the app
  // in offline mode.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    void probeConnectivity();
  }
}

/** Verified offline: a probe of the API has failed and has not since succeeded. */
export function isOffline(): boolean {
  ensureStarted();
  return offline;
}

/** Subscribe to verified connectivity changes. Returns an unsubscribe fn. */
export function subscribeConnectivity(listener: Listener): () => void {
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: reset module state between cases. */
export function __resetConnectivityForTests(): void {
  offline = false;
  started = false;
  inFlight = null;
  stopRecheck();
  listeners.clear();
}
